import { useState, useMemo, useEffect, lazy, Suspense, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Lock, Unlock, Calendar, CalendarDays, Building2, Target, Users, Clock,
  AlertCircle, Eye, BarChart3,
  Settings, Loader2, List, StickyNote, Store,
  Search, LayoutGrid,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { getJson } from "@/lib/http";
import { dbg } from "@/lib/dbg";
import { cn } from "@/lib/utils";
import { getCampaignIntegrityUserMessage } from "@/lib/campaignIntegrityErrors";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear } from "@/state/yearStore";
import { useUrlYear } from "@/hooks/useUrlYear";
import { CycleNotesModal } from "@/components/CycleNotesModal";
import { PreLockCheckModal } from "@/components/cycles/PreLockCheckModal";

// Lazy load calendar component
const CyclesCalendar = lazy(() => import("@/components/CyclesCalendar"));

// Types based on the spec
interface Cycle {
  id: number;
  code: string | number;
  name: string;
  retailerId: number;
  retailerName?: string;
  startDate?: string; // camelCase (optional)
  endDate?: string;
  start_date?: string; // snake_case (optional)
  end_date?: string;
  bookingDeadline?: string;
  status?: string;
  locked_flag: boolean;
  retailer_name?: string; // API returns snake_case (legacy)
  totalCampaigns?: number;
  confirmedCampaigns?: number;
  otherCampaigns?: number;
  hasCampaignSnapshots?: boolean | null;
  lockStatus?: 'locked' | 'empty-lock' | 'unlocked';
}

// Paginated API response type (new unified format)
interface CyclesApiResponse {
  data: Cycle[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const MONTH_STRIP = [
  { m: 1, label: "Jan" },
  { m: 2, label: "Feb" },
  { m: 3, label: "Mar" },
  { m: 4, label: "Apr" },
  { m: 5, label: "May" },
  { m: 6, label: "Jun" },
  { m: 7, label: "Jul" },
  { m: 8, label: "Aug" },
  { m: 9, label: "Sep" },
  { m: 10, label: "Oct" },
  { m: 11, label: "Nov" },
  { m: 12, label: "Dec" },
];

function cycleYearFromStart(c: { dateRange?: { start: Date | null } }): number | null {
  const d = c.dateRange?.start;
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

function cycleStartMonth1to12(c: { dateRange?: { start: Date | null } }): number | null {
  const d = c.dateRange?.start;
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.getMonth() + 1;
}

function cycleSpansMonth(c: { dateRange?: { start: Date | null; end?: Date | null } }, month1to12: number) {
  const startM = cycleStartMonth1to12(c);
  if (startM == null) return false;
  const end = c.dateRange?.end;
  const endM = end && end instanceof Date && !Number.isNaN(end.getTime()) ? end.getMonth() + 1 : startM;
  return month1to12 >= startM && month1to12 <= endM;
}

export default function CyclesDashboardV2() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const urlParams = new URLSearchParams(window.location.search);
  const urlRetailerId = Number(urlParams.get('retailerId'));
  const focusedCycleId = Number(urlParams.get('cycleId'));

  // Global year filter
  const year = useYear();
  useUrlYear();

  // Cycle notes modal state
  const [notesCycleId, setNotesCycleId] = useState<number | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesLocked, setNotesLocked] = useState(false);

  // Pre-lock check modal state
  const [preLockModalOpen, setPreLockModalOpen] = useState(false);
  const [preLockCycleId, setPreLockCycleId] = useState<number | null>(null);
  const [preLockCycleName, setPreLockCycleName] = useState<string>("");

  // Data fetching (mirror original dashboard structure)
  const { data: retailers = [] } = useQuery<any[]>({
    queryKey: ['/api/retailers'],
    queryFn: () => getJson('/api/retailers')
  });

  const { data: campaignsResponse } = useQuery<{ data: any[]; totalCount: number } | any[]>({
    queryKey: ['/api/campaigns'],
    queryFn: () => getJson('/api/campaigns')
  });
  // Handle both paginated response { data: [...] } and legacy array response
  const campaigns = useMemo(() => {
    if (!campaignsResponse) return [];
    if (Array.isArray(campaignsResponse)) return campaignsResponse;
    if (campaignsResponse.data && Array.isArray(campaignsResponse.data)) return campaignsResponse.data;
    return [];
  }, [campaignsResponse]);

  const { data: campaignStatuses = [] } = useQuery<any[]>({
    queryKey: ['/api/campaign-statuses'],
    queryFn: () => getJson('/api/campaign-statuses')
  });

  /** All cycles for the selected year (every page) — powers retailer rail + grid without paging gaps. */
  const {
    data: fullYearCyclesData,
    isLoading,
    error,
    refetch,
  } = useQuery<CyclesApiResponse>({
    queryKey: ['/api/cycles', 'full-year', year],
    queryFn: async () => {
      const pageSize = 100;
      let page = 1;
      let totalCount = 0;
      const all: Cycle[] = [];
      do {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
          year: String(year),
        });
        const response = await getJson<CyclesApiResponse>(`/api/cycles?${params.toString()}`);
        const data = response?.data ?? [];
        totalCount = response?.totalCount ?? 0;
        all.push(...data);
        page += 1;
      } while (all.length < totalCount && totalCount > 0);
      return {
        data: all,
        totalCount,
        page: 1,
        pageSize: all.length,
      } as CyclesApiResponse;
    },
    staleTime: 30_000,
  });

  const yearRawCycles = fullYearCyclesData?.data ?? [];

  const openNotesModal = useCallback((cycleId: number, lockedFlag: boolean) => {
    setNotesCycleId(cycleId);
    setNotesLocked(lockedFlag);
    setNotesModalOpen(true);
  }, []);

  const lockedCycleIds = yearRawCycles.filter((cycle) => cycle.locked_flag).map((cycle) => cycle.id);

  const lockStatusQueries = useQuery({
    queryKey: ['/api/cycles/lock-status', lockedCycleIds],
    queryFn: async () => {
      if (lockedCycleIds.length === 0) return {};

      const response = await fetch(
        `/api/cycles/snapshot-status-batch?cycleIds=${lockedCycleIds.join(',')}`
      );
      if (!response.ok) return {};

      const data: Record<string, { hasCampaignSnapshots: boolean; status: string }> =
        await response.json();

      const statusMap: Record<number, { hasCampaignSnapshots: boolean; status: string }> = {};
      for (const [cycleIdStr, status] of Object.entries(data)) {
        statusMap[Number(cycleIdStr)] = status;
      }
      return statusMap;
    },
    enabled: lockedCycleIds.length > 0,
    staleTime: 30000,
  });

  const cyclesWithLockFields = useMemo(() => {
    return yearRawCycles.map((cycle) => {
      if (cycle.locked_flag) {
        const lockStatus = lockStatusQueries.data?.[cycle.id];
        return {
          ...cycle,
          hasCampaignSnapshots: lockStatus?.hasCampaignSnapshots ?? null,
          lockStatus: lockStatus?.status ?? "locked",
        };
      }
      return {
        ...cycle,
        hasCampaignSnapshots: null,
        lockStatus: "unlocked" as const,
      };
    });
  }, [yearRawCycles, lockStatusQueries.data]);

  // Derived view state (filters)
  const [selectedRetailer, setSelectedRetailer] = useState<number | null>(
    Number.isFinite(urlRetailerId) && urlRetailerId > 0 ? urlRetailerId : null
  );
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft' | 'locked'>('all');
  const [monthJump, setMonthJump] = useState<number | null>(null);
  const [cycleSearch, setCycleSearch] = useState("");

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>(() => {
    try {
      const saved = localStorage.getItem('cycleDashboardPrefs');
      if (saved) {
        const prefs = JSON.parse(saved);
        return prefs.viewMode || 'list';
      }
    } catch (e) {
      // Ignore parse errors
    }
    return 'list';
  });

  // Load saved preferences on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('cycleDashboardPrefs');
      if (saved) {
        const prefs = JSON.parse(saved);
        if ((!Number.isFinite(urlRetailerId) || urlRetailerId <= 0) && prefs.selectedRetailer !== undefined) {
          setSelectedRetailer(prefs.selectedRetailer);
        }
        if (prefs.statusFilter) {
          setStatusFilter(prefs.statusFilter);
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  // Save preferences when they change
  useEffect(() => {
    try {
      localStorage.setItem('cycleDashboardPrefs', JSON.stringify({
        viewMode,
        selectedRetailer,
        statusFilter,
      }));
    } catch (e) {
      // Ignore storage errors
    }
  }, [viewMode, selectedRetailer, statusFilter]);

  // Lock cycle mutation with enhanced feedback
  const lockMutation = useMutation({
    mutationFn: ({ id, acknowledgedDrift }: { id: number; acknowledgedDrift?: boolean }) => {
      dbg.log('[LOCK_DEBUG_CLIENT] sending cycleId =', id, 'acknowledgedDrift =', acknowledgedDrift);
      return getJson(`/api/cycles/${id}/lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          removalGenerationMode: 'confirmed_plus_planned',
          ...(acknowledgedDrift ? { acknowledgedDrift: true } : {}),
        }),
      });
    },
    onSuccess: async (_data: any, { id }) => {
      // Optimistic update for just the affected cycle (handle paginated response)
      queryClient.setQueriesData({ queryKey: ['/api/cycles'] }, (prev: any) => {
        if (!prev) return prev;
        if (prev.data && Array.isArray(prev.data)) {
          return { ...prev, data: prev.data.map((c: Cycle) => c.id === id ? { ...c, locked_flag: true } : c) };
        }
        return prev;
      });

      const count = (_data as any)?.assignmentCount || 0;
      const totalCampaigns = (_data as any)?.totalCampaigns || 0;
      const postLockWarnings = (_data as any)?.postLockWarnings as Array<{ step: string; message: string }> | undefined;

      if (postLockWarnings && postLockWarnings.length > 0) {
        toast({
          title: '⚠️ Cycle Locked with Warnings',
          description: `Locked, but ${postLockWarnings.length} post-lock step(s) failed: ${postLockWarnings.map(w => w.step).join(', ')}. Removals may need manual repair.`,
          variant: 'destructive',
          duration: 10000,
        });
      } else if (totalCampaigns > 0) {
        toast({
          title: '✅ Cycle Locked Successfully',
          description: `Cycle locked with ${count} assignments across ${totalCampaigns} campaigns.`,
          duration: 4000
        });
      } else {
        toast({
          title: '⚠️ Cycle Locked (Empty)',
          description: 'Cycle locked, but no confirmed campaigns were found.',
          variant: 'destructive',
          duration: 6000
        });
      }

      // Refetch authoritative data
      queryClient.invalidateQueries({ queryKey: ['/api/cycles'] });
      queryClient.invalidateQueries({ queryKey: ['/api/booking-cycles'] }); // backward compat
      queryClient.invalidateQueries({ queryKey: ['/api/cycles/lock-status'] });
    },
    onError: (error: any) => {
      toast({
        title: '❌ Failed to Lock Cycle',
        description: getCampaignIntegrityUserMessage(error, 'cycle-lock'),
        variant: 'destructive',
        duration: 5000
      });
    }
  });

  // Unlock cycle mutation with feedback
  const unlockMutation = useMutation({
    mutationFn: (id: number) => {
      return getJson(`/api/cycles/${id}/unlock`, { method: 'POST' });
    },
    onSuccess: (_data, id) => {
      // Optimistic update for just the affected cycle (handle paginated response)
      queryClient.setQueriesData({ queryKey: ['/api/cycles'] }, (prev: any) => {
        if (!prev) return prev;
        if (prev.data && Array.isArray(prev.data)) {
          return { ...prev, data: prev.data.map((c: Cycle) => c.id === id ? { ...c, locked_flag: false } : c) };
        }
        return prev;
      });

      toast({
        title: '✅ Cycle Unlocked Successfully',
        description: 'The cycle has been unlocked and is now available for editing.',
        duration: 4000
      });

      // Refetch authoritative data
      queryClient.invalidateQueries({ queryKey: ['/api/cycles'] });
      queryClient.invalidateQueries({ queryKey: ['/api/booking-cycles'] }); // backward compat
      queryClient.invalidateQueries({ queryKey: ['/api/cycles/lock-status'] });
    },
    onError: (error: any) => {
      const message = error?.message || 'Please try again.';
      toast({
        title: '❌ Failed to Unlock Cycle',
        description: message,
        variant: 'destructive',
        duration: 5000
      });
    }
  });

  const onLock = useCallback((id: number) => {
    const cycle = yearRawCycles.find((c: any) => c.id === id);
    setPreLockCycleId(id);
    setPreLockCycleName(cycle?.name || `Cycle ${id}`);
    setPreLockModalOpen(true);
  }, [yearRawCycles]);

  // Handler for proceeding with lock from the pre-lock check modal
  const handleProceedWithLock = useCallback((options?: { acknowledgedDrift?: boolean }) => {
    if (preLockCycleId) {
      lockMutation.mutate({ id: preLockCycleId, acknowledgedDrift: options?.acknowledgedDrift });
    }
  }, [preLockCycleId, lockMutation]);

  const onUnlock = useCallback((id: number) => {
    unlockMutation.mutate(id);
  }, [unlockMutation]);

  const isLocking = lockMutation.isPending || unlockMutation.isPending;


  // Enrich cycles to match original dashboard data model
  const cyclesWithDetails = useMemo(() => {
    return (cyclesWithLockFields as any[]).map((cycle) => {
      // Support snake_case or camelCase dates
      const startISO = cycle.startDate || cycle.start_date || null;
      const endISO = cycle.endDate || cycle.end_date || null;
      const dateRange = {
        start: startISO ? new Date(startISO) : null,
        end: endISO ? new Date(endISO) : null,
      };

      const retailer = (retailers as any[]).find(r => r.id === cycle.retailerId) || {
        id: cycle.retailerId,
        name: (cycle as any).retailerName || cycle.retailer_name || 'Retailer'
      };

      // Use campaign counts from API if available (more accurate for locked cycles)
      // Fall back to calculating from campaigns array if API doesn't provide them
      let totalCampaigns = cycle.totalCampaigns;
      let confirmedCampaigns = cycle.confirmedCampaigns;
      let otherCampaigns = cycle.otherCampaigns;

      if (totalCampaigns === undefined || totalCampaigns === null) {
        const cycleCampaigns = (campaigns as any[]).filter(c => c.cycleId === cycle.id);
        // Exclude Removed (8) and Archived (9) from totals
        const activeCampaigns = cycleCampaigns.filter(c => c.statusId !== 8 && c.statusId !== 9);
        // Only status_id = 2 is "Confirmed"
        confirmedCampaigns = activeCampaigns.filter(c => c.statusId === 2).length;
        totalCampaigns = activeCampaigns.length;
        otherCampaigns = totalCampaigns - confirmedCampaigns;
      }

      const isLocked = !!cycle.locked_flag;

      const cycleStoreCount =
        Number((cycle as any).cycleStoreCount ?? (cycle as any).cycle_store_count ?? 0) || 0;
      const cycleOperatorCount =
        Number((cycle as any).cycleOperatorCount ?? (cycle as any).cycle_operator_count ?? 0) || 0;

      // Per-card verification log
      try {
        const code = cycle.code ?? cycle.name;
        dbg.log('[CYCLE_DEBUG]', { id: cycle.id, code, locked_flag: isLocked, summary: { totalCampaigns, confirmedCampaigns, otherCampaigns } });
        dbg.log('[UI_ICON_DEBUG]', { id: cycle.id, code, locked_flag: isLocked });
      } catch { }

      return {
        ...cycle,
        retailer,
        dateRange,
        totalCampaigns,
        confirmedCampaigns,
        otherCampaigns,
        cycleStoreCount,
        cycleOperatorCount,
        isLocked,
      };
    });
  }, [cyclesWithLockFields, campaigns, retailers]);

  useEffect(() => {
    if (!retailers.length) return;
    if (Number.isFinite(urlRetailerId) && urlRetailerId > 0) return;
    setSelectedRetailer((prev) => {
      if (prev != null && prev > 0) return prev;
      return retailers[0]?.id ?? null;
    });
  }, [retailers, urlRetailerId]);

  const railItems = useMemo(() => {
    const sorted = [...(retailers as any[])].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return sorted.map((retailer) => {
      const list = cyclesWithDetails.filter((c: any) => {
        const rid = c.retailer?.id ?? c.retailerId;
        return rid === retailer.id && cycleYearFromStart(c) === year;
      });
      const locked = list.filter((c: any) => c.isLocked).length;
      const campaigns = list.reduce((s, c: any) => s + (c.totalCampaigns || 0), 0);
      return { retailer, count: list.length, locked, campaigns };
    });
  }, [retailers, cyclesWithDetails, year]);

  const filteredTileCycles = useMemo(() => {
    return cyclesWithDetails
      .filter((c: any) => {
        const rid = c.retailer?.id ?? c.retailerId;
        if (selectedRetailer != null && rid !== selectedRetailer) return false;
        const cy = cycleYearFromStart(c);
        if (cy != null && cy !== year) return false;
        if (statusFilter === "active" && (!(c.totalCampaigns > 0) || c.isLocked)) return false;
        if (statusFilter === "draft" && !(((c.totalCampaigns ?? 0) === 0) && !c.isLocked)) return false;
        if (statusFilter === "locked" && !c.isLocked) return false;
        if (monthJump != null && !cycleSpansMonth(c, monthJump)) return false;
        const q = cycleSearch.trim().toLowerCase();
        if (q) {
          const code = String(c.code ?? c.name ?? "");
          if (!code.toLowerCase().includes(q) && !String(c.id).includes(q)) return false;
        }
        return true;
      })
      .sort((a: any, b: any) => {
        const aStart = a.dateRange?.start || a.start_date || a.startDate;
        const bStart = b.dateRange?.start || b.start_date || b.startDate;
        if (!aStart && !bStart) return 0;
        if (!aStart) return 1;
        if (!bStart) return -1;
        const aDate = aStart instanceof Date ? aStart : new Date(aStart);
        const bDate = bStart instanceof Date ? bStart : new Date(bStart);
        return aDate.getTime() - bDate.getTime();
      });
  }, [cyclesWithDetails, selectedRetailer, year, statusFilter, monthJump, cycleSearch]);

  const filteredCycles = useMemo(() => {
    if (viewMode !== "calendar") return [];
    const retailerLookup = new Map<number, any>((retailers as any[]).map((r) => [r.id, r]));
    return filteredTileCycles.map((cycle: any) => {
      const startISO = cycle.startDate || cycle.start_date || null;
      const endISO = cycle.endDate || cycle.end_date || null;
      const dateRange = {
        start: startISO ? new Date(startISO) : null,
        end: endISO ? new Date(endISO) : null,
      };
      const retailer =
        retailerLookup.get(cycle.retailerId) || {
          id: cycle.retailerId,
          name: cycle.retailer_name || "Retailer",
        };
      return {
        ...cycle,
        retailer,
        dateRange,
        isLocked: !!cycle.locked_flag,
        totalCampaigns: cycle.totalCampaigns ?? 0,
        confirmedCampaigns: cycle.confirmedCampaigns ?? 0,
        otherCampaigns: cycle.otherCampaigns ?? 0,
        cycleStoreCount: Number(cycle.cycleStoreCount ?? cycle.cycle_store_count ?? 0) || 0,
        cycleOperatorCount: Number(cycle.cycleOperatorCount ?? cycle.cycle_operator_count ?? 0) || 0,
      };
    });
  }, [viewMode, filteredTileCycles, retailers]);

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Failed to load cycles</span>
            </div>
            <p className="text-red-600 mt-2">
              {error instanceof Error ? error.message : "An unknown error occurred"}
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              className="mt-4"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6 space-y-6">
        <PageHeader
          icon={Calendar}
          title="Cycles Dashboard"
          description="Manage booking cycles, lock periods, and track campaign progress across retailers"
        />

        {/* Summary header */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Campaigns</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{cyclesWithDetails.reduce((sum, c: any) => sum + (c.totalCampaigns || 0), 0)}</div>
              <p className="text-xs text-muted-foreground">Across all cycles</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Draft</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {cyclesWithDetails.filter((c: any) => !c.isLocked && !((c.totalCampaigns ?? 0) > 0)).length}
              </div>
              <p className="text-xs text-muted-foreground">In preparation</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {cyclesWithDetails.filter((c: any) => !c.isLocked && (c.totalCampaigns ?? 0) > 0).length}
              </div>
              <p className="text-xs text-muted-foreground">Running now</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Locked</CardTitle>
              <Lock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{cyclesWithDetails.filter((c: any) => c.isLocked).length}</div>
              <p className="text-xs text-muted-foreground">Sign-off ready</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Cycles</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{cyclesWithDetails.length}</div>
              <p className="text-xs text-muted-foreground">Booking periods</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Retailers</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{new Set(cyclesWithDetails.map((c: any) => c.retailer?.id ?? c.retailerId)).size}</div>
              <p className="text-xs text-muted-foreground">With active cycles</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters: year, status, search, month jump */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">Filters</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <YearFilter />
              <div className="hidden h-6 w-px bg-border sm:block" />
              {[
                { id: "all" as const, label: "All" },
                { id: "active" as const, label: "Active" },
                { id: "draft" as const, label: "Draft" },
                { id: "locked" as const, label: "Locked" },
              ].map((chip) => (
                <Button
                  key={chip.id}
                  variant={statusFilter === chip.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setStatusFilter(chip.id);
                    setMonthJump(null);
                  }}
                >
                  {chip.label}
                </Button>
              ))}
              <div className="ml-auto w-full min-w-[200px] max-w-xs sm:w-72">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search cycle name or id…"
                    className="pl-8"
                    value={cycleSearch}
                    onChange={(e) => setCycleSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" />
                Jump by month
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant={monthJump === null ? "secondary" : "ghost"}
                  className="h-8 text-xs"
                  onClick={() => setMonthJump(null)}
                >
                  All months
                </Button>
                {MONTH_STRIP.map(({ m, label }) => {
                  const has = cyclesWithDetails.some(
                    (c) =>
                      (c.retailer?.id ?? c.retailerId) === selectedRetailer &&
                      cycleYearFromStart(c) === year &&
                      cycleSpansMonth(c, m),
                  );
                  return (
                    <Button
                      key={m}
                      type="button"
                      size="sm"
                      variant={monthJump === m ? "default" : "outline"}
                      className={cn("h-8 min-w-[2.5rem] px-2 text-xs", !has && "opacity-40")}
                      disabled={!has || selectedRetailer == null}
                      onClick={() => setMonthJump(m)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">View:</span>
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(value) => {
                  if (value === "list" || value === "calendar") {
                    setViewMode(value);
                  }
                }}
              >
                <ToggleGroupItem value="list" aria-label="Tile view">
                  <List className="mr-2 h-4 w-4" />
                  Tile View
                </ToggleGroupItem>
                <ToggleGroupItem value="calendar" aria-label="Calendar view">
                  <Calendar className="mr-2 h-4 w-4" />
                  Calendar View
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
          <Card className="h-fit lg:sticky lg:top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Retailers</CardTitle>
              <CardDescription>Select a retailer to load every cycle for {year} in the grid.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {railItems.map(({ retailer, count, locked, campaigns }) => (
                <button
                  key={retailer.id}
                  type="button"
                  onClick={() => {
                    setSelectedRetailer(retailer.id);
                    setMonthJump(null);
                  }}
                  className={cn(
                    "flex w-full flex-col gap-1 rounded-lg border bg-card p-3 text-left text-sm transition hover:border-primary/50 hover:shadow-sm",
                    selectedRetailer === retailer.id && "border-primary ring-2 ring-primary/15",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-semibold">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {retailer.name}
                    </span>
                    {count > 0 ? (
                      <Badge variant="outline" className="shrink-0">
                        {count}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        none
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    <span>{campaigns} campaigns</span>
                    <span>{locked} locked</span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <div className="space-y-4">
            {selectedRetailer != null && (
              <div className="flex flex-wrap items-baseline justify-between gap-2 px-1">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <LayoutGrid className="h-5 w-5 text-primary" />
                  {(retailers as any[]).find((r) => r.id === selectedRetailer)?.name ?? "Retailer"}{" "}
                  <span className="font-normal text-muted-foreground">
                    · {year} · {filteredTileCycles.length} cycle{filteredTileCycles.length === 1 ? "" : "s"} shown
                  </span>
                </h2>
              </div>
            )}

            {isLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center gap-2 py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Loading cycles…</span>
                </CardContent>
              </Card>
            ) : viewMode === "calendar" ? (
              <Suspense
                fallback={
                  <Card>
                    <CardContent className="flex items-center justify-center gap-2 py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      <span className="text-muted-foreground">Loading calendar…</span>
                    </CardContent>
                  </Card>
                }
              >
                <CyclesCalendar
                  cycles={filteredCycles}
                  retailers={retailers}
                  showAllEvents={false}
                  onCycleUpdate={() => {
                    queryClient.invalidateQueries({ queryKey: ["/api/cycles"] });
                    queryClient.invalidateQueries({ queryKey: ["/api/booking-cycles"] });
                    queryClient.invalidateQueries({ queryKey: ["/api/retailers"] });
                  }}
                  onOpenNotes={(cycleId, lockedFlag) => openNotesModal(cycleId, lockedFlag)}
                />
              </Suspense>
            ) : filteredTileCycles.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
                  <Calendar className="h-12 w-12 text-muted-foreground/40" />
                  <p className="text-lg font-medium text-muted-foreground">No cycles match</p>
                  <p className="max-w-md text-sm text-muted-foreground">
                    Try another year or retailer, clear the month filter, or adjust status. All cycles for the selected retailer and year are loaded (no paging).
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setStatusFilter("all");
                      setMonthJump(null);
                      setCycleSearch("");
                    }}
                  >
                    Reset filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {filteredTileCycles.map((cycle: any) => {
                  const code = cycle.code ?? cycle.name;
                  const start = cycle.dateRange?.start;
                  const end = cycle.dateRange?.end;
                  const dateText =
                    start && end
                      ? `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                      : "";
                  return (
                    <Card
                      key={cycle.id}
                      className={cn(
                        "border-2 transition-shadow hover:shadow-md",
                        cycle.isLocked ? "border-red-200 bg-red-50/50" : "border-border bg-card",
                        Number.isFinite(focusedCycleId) && focusedCycleId === cycle.id && "border-primary ring-2 ring-primary/20",
                      )}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <h4 className="font-semibold">Cycle {code}</h4>
                          {cycle.isLocked && <Lock className="h-4 w-4 text-red-600" aria-hidden />}
                        </div>
                        {dateText && <p className="text-sm text-muted-foreground">{dateText}</p>}
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-3 gap-2 text-center text-sm">
                          <div className="rounded-md bg-muted/50 py-2">
                            <div className="font-bold">{cycle.totalCampaigns}</div>
                            <div className="text-[10px] text-muted-foreground">Total</div>
                          </div>
                          <div className="rounded-md bg-emerald-50 py-2 dark:bg-emerald-950/40">
                            <div className="font-bold text-emerald-700 dark:text-emerald-400">{cycle.confirmedCampaigns}</div>
                            <div className="text-[10px] text-muted-foreground">Confirmed</div>
                          </div>
                          <div className="rounded-md bg-amber-50 py-2 dark:bg-amber-950/40">
                            <div className="font-bold text-amber-800 dark:text-amber-400">{cycle.otherCampaigns}</div>
                            <div className="text-[10px] text-muted-foreground">Other</div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5">
                            <Store className="h-4 w-4 shrink-0" aria-hidden />
                            <span>
                              {cycle.cycleStoreCount ?? 0}{" "}
                              {(cycle.cycleStoreCount ?? 0) === 1 ? "store" : "stores"}
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Users className="h-4 w-4 shrink-0" aria-hidden />
                            <span>
                              {cycle.cycleOperatorCount ?? 0}{" "}
                              {(cycle.cycleOperatorCount ?? 0) === 1 ? "operator" : "operators"}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Link href={`/?cycleId=${cycle.id}`}>
                                  <Button variant="outline" size="icon" className="h-9 w-9" aria-label="View cycle">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent>View cycle</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Link href={`/cycles/${cycle.id}/snapshots`}>
                                  <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Snapshots">
                                    <BarChart3 className="h-4 w-4" />
                                  </Button>
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent>Snapshots</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Link href={`/admin/proscriptive/${cycle.id}`}>
                                  <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Proscriptive workflow">
                                    <Settings className="h-4 w-4" />
                                  </Button>
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent>Proscriptive workflow</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9"
                                  aria-label="Cycle notes"
                                  onClick={() => openNotesModal(cycle.id, !!cycle.locked_flag)}
                                >
                                  <StickyNote className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Cycle notes</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {!cycle.isLocked ? (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onLock(cycle.id)}
                                    disabled={lockMutation.isPending || unlockMutation.isPending}
                                    className="h-9 w-9 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    aria-label="Lock cycle"
                                  >
                                    {lockMutation.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Lock className="h-4 w-4" />
                                    )}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onUnlock(cycle.id)}
                                    disabled={lockMutation.isPending || unlockMutation.isPending}
                                    className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    aria-label="Unlock cycle"
                                  >
                                    {unlockMutation.isPending ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Unlock className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </TooltipTrigger>
                              <TooltipContent>
                                {!cycle.isLocked
                                  ? "Lock cycle"
                                  : cycle.lockStatus === "empty-lock"
                                    ? "Unlock empty lock"
                                    : "Unlock cycle"}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <CycleNotesModal
        cycleId={notesCycleId}
        isOpen={notesModalOpen}
        locked={notesLocked}
        onClose={() => setNotesModalOpen(false)}
      />
      {preLockCycleId && (
        <PreLockCheckModal
          open={preLockModalOpen}
          onOpenChange={setPreLockModalOpen}
          cycleId={preLockCycleId}
          cycleName={preLockCycleName}
          onProceedWithLock={handleProceedWithLock}
          isLocking={lockMutation.isPending}
        />
      )}
    </>
  );
}
